import { useCallback, useEffect, useState } from 'react'
import { Navigate, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { trainingApi, userApi, trainingProgramActivationApi, trainingProgramApi } from '../../api'
import { subscriptionApi } from '../../api/subscriptionApi'
import { workoutApi, type WorkoutSession, type WorkoutSessionSummary } from '../../api/workoutApi'
import type { ApiError } from '../../api/client'
import type { TrainingProgram, TrainingProgramActivation, TrainingSet, UserRelation } from '../../types/api'
import { useAuth } from '../../contexts/AuthContext'
import { entitlementsOf } from '../../utils/entitlements'
import { useTheme } from '../../hooks/useTheme'
import { useTranslation } from '../../i18n/useTranslation'
import { TRAINING_SET_STATUS, USER_RELATION_STATUS } from '../../constants'
import { formatShortDate } from '../../utils/dateTranslations'
import { Button, Card, Loading, TrainingSetCard } from '../ui'
import AssignProgramSheet from './AssignProgramSheet'

type HistoryTab = 'sets' | 'programs' | 'workouts'

interface HistorySetItem {
  trainingSet: TrainingSet
  /** Other party in the set (coach or trainee) — used for navigation */
  counterpartId: number
  isCurrentUserCoach: boolean
}

interface HistoryProgramItem {
  activation: TrainingProgramActivation
}

interface HistoryWorkoutItem {
  summary: WorkoutSessionSummary
  traineeId?: number
}

function relationLabel(user: UserRelation | null | undefined, fallback = ''): string {
  if (!user) return fallback
  if (user.firstName) return `${user.firstName} ${user.lastName || ''}`.trim()
  return user.username ? `@${user.username}` : fallback
}

function formatDuration(startedAt: string, finishedAt: string): string {
  const start = new Date(startedAt).getTime()
  const end = new Date(finishedAt).getTime()
  if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start) return ''
  const mins = Math.round((end - start) / 60000)
  if (mins < 60) return `${mins}m`
  const h = Math.floor(mins / 60)
  const m = mins % 60
  return m > 0 ? `${h}h ${m}m` : `${h}h`
}

export default function HistoryPage() {
  const { id: routeTraineeId } = useParams<{ id?: string }>()
  const [searchParams, setSearchParams] = useSearchParams()
  const navigate = useNavigate()
  const theme = useTheme()
  const { t, language } = useTranslation()
  const { user, refreshUser } = useAuth()
  const trialAvailable = Boolean(user?.trialAvailable)

  const clientId = routeTraineeId ? parseInt(routeTraineeId, 10) : null
  const isClientScope = clientId != null && Number.isFinite(clientId)
  const showSetsTab = isClientScope || entitlementsOf(user).hasActiveCoach

  const tabParam = searchParams.get('tab')
  const tab: HistoryTab = (() => {
    if (tabParam === 'workouts' || tabParam === 'programs') return tabParam
    if (tabParam === 'sets' && showSetsTab) return 'sets'
    return showSetsTab ? 'sets' : 'programs'
  })()

  const [client, setClient] = useState<UserRelation | null>(null)
  const [coachPrograms, setCoachPrograms] = useState<TrainingProgram[]>([])
  const [sets, setSets] = useState<HistorySetItem[]>([])
  const [programs, setPrograms] = useState<HistoryProgramItem[]>([])
  const [workouts, setWorkouts] = useState<HistoryWorkoutItem[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [workoutEntitlementMissing, setWorkoutEntitlementMissing] = useState(false)
  const [savingProgramId, setSavingProgramId] = useState<number | null>(null)
  const [showAssignSheet, setShowAssignSheet] = useState(false)
  const [assignSheetInitialProgramId, setAssignSheetInitialProgramId] = useState<number | undefined>()
  const [expandedWorkoutId, setExpandedWorkoutId] = useState<number | null>(null)
  const [workoutDetail, setWorkoutDetail] = useState<WorkoutSession | null>(null)
  const [workoutDetailLoading, setWorkoutDetailLoading] = useState(false)
  const [deletingWorkoutId, setDeletingWorkoutId] = useState<number | null>(null)
  const [trialBusy, setTrialBusy] = useState(false)

  const setTab = (next: HistoryTab) => {
    setSearchParams(next === 'sets' ? {} : { tab: next }, { replace: true })
  }

  const startTrial = useCallback(async () => {
    try {
      setTrialBusy(true)
      await subscriptionApi.activateTrial()
      await refreshUser()
      navigate('/subscription', { replace: true })
    } catch (err) {
      console.error('Failed to activate trial:', err)
    } finally {
      setTrialBusy(false)
    }
  }, [navigate, refreshUser])

  useEffect(() => {
    if (!showSetsTab && tabParam === 'sets') {
      setSearchParams({ tab: 'programs' }, { replace: true })
    }
  }, [showSetsTab, tabParam, setSearchParams])

  const load = useCallback(async () => {
    if (!user?.id) return
    setLoading(true)
    setError(null)
    setWorkoutEntitlementMissing(false)
    try {
      if (isClientScope) {
        const trainees = await userApi.getTrainees()
        const found = trainees.find(tr => tr.id === clientId) ?? null
        if (!found) {
          setError(t.historyPage.error.clientNotFound)
          setClient(null)
          setSets([])
          setPrograms([])
          setWorkouts([])
          return
        }
        setClient(found)

        const [setList, assignmentHistory, workoutList, programsList] = await Promise.all([
          trainingApi.getTraineeSets(clientId).catch(() => [] as TrainingSet[]),
          trainingProgramActivationApi.getTraineeAssignmentHistory(clientId).catch(() => [] as TrainingProgramActivation[]),
          entitlementsOf(user).clientWorkoutHistory
            ? workoutApi.coachListHistory(clientId).catch((err: ApiError) => {
                if (err?.status === 403) {
                  setWorkoutEntitlementMissing(true)
                }
                return [] as WorkoutSessionSummary[]
              })
            : Promise.resolve([] as WorkoutSessionSummary[]).then(list => {
                setWorkoutEntitlementMissing(true)
                return list
              }),
          trainingProgramApi.getPrograms().catch(() => [] as TrainingProgram[]),
        ])
        setCoachPrograms(programsList)

        setSets(
          setList
            .filter(s => s.status === TRAINING_SET_STATUS.COMPLETED)
            .sort((a, b) =>
              new Date(b.calculatedEndDate).getTime() - new Date(a.calculatedEndDate).getTime())
            .map(s => ({
              trainingSet: s,
              counterpartId: clientId,
              isCurrentUserCoach: true,
            })),
        )

        // Past assignments only — same as client details history
        setPrograms(
          assignmentHistory
            .filter(a => !a.isActive)
            .sort((a, b) => new Date(b.startedAt).getTime() - new Date(a.startedAt).getTime())
            .map(activation => ({ activation })),
        )

        setWorkouts(
          workoutList
            .sort((a, b) => new Date(b.finishedAt).getTime() - new Date(a.finishedAt).getTime())
            .map(summary => ({ summary, traineeId: clientId })),
        )
      } else {
        setClient(null)
        setCoachPrograms([])

        if (entitlementsOf(user).hasActiveCoach) {
          const coaches = (user.hasCoaches ?? false)
            ? (await userApi.getCoaches()).filter(r => r.relationStatus === 'ACTIVE')
            : []

          const perCoachSets = await Promise.all(
            coaches.map(async (coach) => {
              const list = await trainingApi.getCoachSets(coach.id).catch(() => [] as TrainingSet[])
              return list
                .filter(s => s.status === TRAINING_SET_STATUS.COMPLETED)
                .map(s => ({
                  trainingSet: s,
                  counterpartId: coach.id,
                  isCurrentUserCoach: false,
                }))
            }),
          )
          setSets(
            perCoachSets.flat().sort((a, b) =>
              new Date(b.trainingSet.calculatedEndDate).getTime()
              - new Date(a.trainingSet.calculatedEndDate).getTime()),
          )
        } else {
          setSets([])
        }

        const selfHistory = await trainingProgramActivationApi.getHistory().catch(() => [] as TrainingProgramActivation[])
        setPrograms(
          selfHistory
            .sort((a, b) => new Date(b.startedAt).getTime() - new Date(a.startedAt).getTime())
            .map(activation => ({ activation })),
        )

        if (!entitlementsOf(user).workoutHistory) {
          setWorkoutEntitlementMissing(true)
          setWorkouts([])
        } else {
          try {
            const own = await workoutApi.listHistory()
            setWorkouts(
              own
                .sort((a, b) => new Date(b.finishedAt).getTime() - new Date(a.finishedAt).getTime())
                .map(summary => ({ summary })),
            )
          } catch (err) {
            if ((err as ApiError)?.status === 403) {
              setWorkoutEntitlementMissing(true)
              setWorkouts([])
            } else {
              throw err
            }
          }
        }
      }
    } catch (err) {
      setError((err as ApiError).message || t.historyPage.error.loadFailed)
    } finally {
      setLoading(false)
    }
  }, [user, isClientScope, clientId, t])

  useEffect(() => {
    load()
  }, [load])

  async function handleSaveProgram(activation: TrainingProgramActivation) {
    setSavingProgramId(activation.programId)
    try {
      await trainingProgramApi.saveProgram(activation.programId)
      setPrograms(prev => prev.map(item =>
        item.activation.programId === activation.programId
          ? { ...item, activation: { ...item.activation, programIsSaved: true } }
          : item,
      ))
    } catch (err) {
      alert((err as ApiError).message || t.trainingPrograms.error.loadFailed)
    } finally {
      setSavingProgramId(null)
    }
  }

  async function toggleWorkout(item: HistoryWorkoutItem) {
    if (expandedWorkoutId === item.summary.id) {
      setExpandedWorkoutId(null)
      setWorkoutDetail(null)
      return
    }
    setExpandedWorkoutId(item.summary.id)
    setWorkoutDetail(null)
    setWorkoutDetailLoading(true)
    try {
      const detail = item.traineeId != null
        ? await workoutApi.coachGetSession(item.summary.id)
        : await workoutApi.getSession(item.summary.id)
      setWorkoutDetail(detail)
    } catch (err) {
      alert((err as ApiError).message || t.workout.error.loadSessionFailed)
      setExpandedWorkoutId(null)
    } finally {
      setWorkoutDetailLoading(false)
    }
  }

  async function handleDeleteWorkout(item: HistoryWorkoutItem) {
    const name = item.summary.blockName || t.historyPage.workouts.unnamedBlock
    if (!confirm(t.historyPage.workouts.deleteConfirm.replace('{{name}}', name))) return
    setDeletingWorkoutId(item.summary.id)
    try {
      if (item.traineeId != null) {
        await workoutApi.coachCancelSession(item.summary.id)
      } else {
        await workoutApi.cancelSession(item.summary.id)
      }
      setWorkouts(prev => prev.filter(w => w.summary.id !== item.summary.id))
      if (expandedWorkoutId === item.summary.id) {
        setExpandedWorkoutId(null)
        setWorkoutDetail(null)
      }
    } catch (err) {
      alert((err as ApiError).message || t.historyPage.workouts.deleteFailed)
    } finally {
      setDeletingWorkoutId(null)
    }
  }

  const tabs: { id: HistoryTab; label: string }[] = [
    ...(showSetsTab ? [{ id: 'sets' as const, label: t.historyPage.tabs.sets }] : []),
    { id: 'programs', label: t.historyPage.tabs.programs },
    { id: 'workouts', label: t.historyPage.tabs.workouts },
  ]

  const title = isClientScope
    ? t.historyPage.clientTitle.replace('{{name}}', relationLabel(client, t.historyPage.title))
    : t.historyPage.title

  if (loading) return <Loading fullScreen />

  return (
    <div className="p-5 pb-24">
      <header className="mb-5">
        <h1 className="text-2xl font-bold" style={{ color: theme.text.primary }}>
          {title}
        </h1>
      </header>

      <div className="flex gap-2 overflow-x-auto pb-1 mb-4" style={{ scrollbarWidth: 'none' }}>
        {tabs.map(tabItem => (
          <button
            key={tabItem.id}
            type="button"
            onClick={() => setTab(tabItem.id)}
            className="px-3.5 py-1.5 rounded-full text-xs font-semibold flex-shrink-0 transition-colors"
            style={tab === tabItem.id
              ? { backgroundColor: theme.buttonColor, color: theme.buttonTextColor }
              : { backgroundColor: theme.cardBg, border: `1px solid ${theme.cardBorder}`, color: theme.text.muted }}
          >
            {tabItem.label}
          </button>
        ))}
      </div>

      {error && (
        <div className="rounded-xl p-4 mb-4 bg-red-500/10 border border-red-500/25">
          <p className="text-sm text-red-500">{error}</p>
        </div>
      )}

      {tab === 'sets' && (
        <>
          <Hint theme={theme}>{t.userDetail.history.retentionHint}</Hint>
          {sets.length === 0 ? (
            <Empty message={t.userDetail.history.noHistory} theme={theme} />
          ) : (
            <div className="space-y-3">
              {sets.map(item => (
                <TrainingSetCard
                  key={item.trainingSet.id}
                  trainingSet={item.trainingSet}
                  userType={item.isCurrentUserCoach ? 'trainee' : 'coach'}
                  userId={String(item.counterpartId)}
                  variant="history"
                  onClick={() => {
                    const userType = item.isCurrentUserCoach ? 'trainee' : 'coach'
                    navigate(`/${userType}/${item.counterpartId}/set/${item.trainingSet.id}`)
                  }}
                />
              ))}
            </div>
          )}
        </>
      )}

      {tab === 'programs' && (
        <>
          <Hint theme={theme}>
            {isClientScope
              ? t.userDetail.programs.unsavedRetentionHint
              : t.trainingPrograms.history.unsavedRetentionHint}
          </Hint>
          {programs.length === 0 ? (
            <Empty
              message={isClientScope ? t.userDetail.programs.noHistory : t.trainingPrograms.history.empty}
              theme={theme}
            />
          ) : (
            <div className="space-y-3">
              {programs.map(({ activation }) => {
                const isFromCoach = activation.assignedByCoachId != null
                const isSaved = activation.programIsSaved
                  ?? (isClientScope && coachPrograms.some(p => p.id === activation.programId))
                  ?? false
                const canOpen = isClientScope || !isFromCoach || isSaved !== false
                const showSave = !isSaved && (isClientScope || !isFromCoach)
                const canReassign = isClientScope
                  && client?.relationStatus === USER_RELATION_STATUS.ACTIVE

                return (
                  <div
                    key={activation.id}
                    className="rounded-2xl overflow-hidden"
                    style={{ backgroundColor: theme.cardBg, border: `1px solid ${theme.cardBorder}` }}
                  >
                    <div className="px-4 py-4">
                      {canOpen ? (
                        <button
                          type="button"
                          className="w-full text-left flex items-center gap-3 active:opacity-70"
                          onClick={() => navigate(`/training-programs/${activation.programId}?readonly=1`)}
                        >
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium truncate" style={{ color: theme.text.primary }}>
                              {activation.programName}
                            </p>
                            <p className="text-xs mt-0.5" style={{ color: theme.text.muted }}>
                              {new Date(activation.startedAt).toLocaleDateString(language, { day: 'numeric', month: 'short' })}
                              {' – '}
                              {activation.endedAt
                                ? new Date(activation.endedAt).toLocaleDateString(language, {
                                    day: 'numeric',
                                    month: 'short',
                                    year: 'numeric',
                                  })
                                : '—'}
                            </p>
                          </div>
                          <span className="text-xl opacity-50 shrink-0" aria-hidden>→</span>
                        </button>
                      ) : (
                        <div className="flex items-center gap-3">
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium truncate" style={{ color: theme.text.primary }}>
                              {activation.programName}
                            </p>
                            <p className="text-xs mt-0.5" style={{ color: theme.text.muted }}>
                              {new Date(activation.startedAt).toLocaleDateString(language, { day: 'numeric', month: 'short' })}
                              {' – '}
                              {activation.endedAt
                                ? new Date(activation.endedAt).toLocaleDateString(language, {
                                    day: 'numeric',
                                    month: 'short',
                                    year: 'numeric',
                                  })
                                : '—'}
                            </p>
                          </div>
                        </div>
                      )}
                      {showSave && (
                        <button
                          type="button"
                          disabled={savingProgramId === activation.programId}
                          className="mt-3 w-full text-xs font-semibold py-2.5 rounded-xl flex items-center justify-center gap-2"
                          style={{
                            color: theme.accent.text,
                            backgroundColor: `${theme.buttonColor}12`,
                            border: `1px solid ${theme.buttonColor}25`,
                          }}
                          onClick={() => handleSaveProgram(activation)}
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z"/>
                          </svg>
                          {savingProgramId === activation.programId
                            ? t.trainingPrograms.history.saving
                            : t.userDetail.programs.saveToLibrary}
                        </button>
                      )}
                    </div>
                    {canReassign && (
                      <div
                        className="flex items-center justify-between px-3 py-2"
                        style={{ borderTop: `1px solid ${theme.cardBorder}` }}
                      >
                        <button
                          type="button"
                          onClick={() => {
                            setAssignSheetInitialProgramId(activation.programId)
                            setShowAssignSheet(true)
                          }}
                          className="px-3 py-3 rounded-xl text-sm font-semibold transition-colors"
                          style={{ color: theme.accent.text }}
                        >
                          {t.userDetail.programs.reassignButton}
                        </button>
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          )}
        </>
      )}

      {tab === 'workouts' && (
        <WorkoutsTab
          items={workouts}
          theme={theme}
          t={t}
          entitlementMissing={workoutEntitlementMissing}
          showSoloUnlock={!isClientScope && !entitlementsOf(user).hasActiveCoach && !user?.hasTrainees}
          expandedId={expandedWorkoutId}
          detail={workoutDetail}
          detailLoading={workoutDetailLoading}
          deletingId={deletingWorkoutId}
          onToggle={toggleWorkout}
          onDelete={handleDeleteWorkout}
          trialAvailable={trialAvailable}
          trialBusy={trialBusy}
          onStartTrial={() => void startTrial()}
          onUnlock={() => navigate('/subscription')}
        />
      )}

      {showAssignSheet && isClientScope && client && (
        <AssignProgramSheet
          programs={coachPrograms}
          traineeId={client.id}
          initialProgramId={assignSheetInitialProgramId}
          onClose={() => setShowAssignSheet(false)}
          onAssigned={() => {
            setShowAssignSheet(false)
            load()
          }}
        />
      )}
    </div>
  )
}

/** Redirect old program history route into the unified History page. */
export function ProgramHistoryRedirect() {
  return <Navigate to="/history?tab=programs" replace />
}

function WorkoutsTab({
  items,
  theme,
  t,
  entitlementMissing,
  showSoloUnlock,
  expandedId,
  detail,
  detailLoading,
  deletingId,
  onToggle,
  onDelete,
  trialAvailable,
  trialBusy,
  onStartTrial,
  onUnlock,
}: {
  items: HistoryWorkoutItem[]
  theme: ReturnType<typeof useTheme>
  t: ReturnType<typeof useTranslation>['t']
  entitlementMissing: boolean
  showSoloUnlock: boolean
  expandedId: number | null
  detail: WorkoutSession | null
  detailLoading: boolean
  deletingId: number | null
  onToggle: (item: HistoryWorkoutItem) => void
  onDelete: (item: HistoryWorkoutItem) => void
  trialAvailable: boolean
  trialBusy: boolean
  onStartTrial: () => void
  onUnlock: () => void
}) {
  if (entitlementMissing && items.length === 0) {
    if (showSoloUnlock || trialAvailable) {
      return (
        <div className="flex flex-col gap-4">
          <Card noHover>
            <p className="text-sm font-semibold" style={{ color: theme.text.primary }}>
              {t.historyPage.workouts.unlockTitle}
            </p>
            <p className="text-sm leading-relaxed" style={{ color: theme.text.secondary }}>
              {t.historyPage.workouts.unlockBody}
            </p>
            {trialAvailable && (
              <Button type="button" variant="brand" size="md" fullWidth className="mt-3" onClick={onStartTrial}>
                {trialBusy ? t.common.loading : t.subscription.trial.activate}
              </Button>
            )}
            {showSoloUnlock && (
              <Button type="button" variant="primary" size="md" fullWidth className="mt-1" onClick={onUnlock}>
                {t.subscription.unlockCta}
              </Button>
            )}
          </Card>

          {showSoloUnlock && (
            <>
              <div className="flex items-center gap-3">
                <div className="flex-1 h-px" style={{ backgroundColor: theme.cardBorder }} />
                <span className="text-xs" style={{ color: theme.text.muted }}>{t.createTrainingBlock.orDivider}</span>
                <div className="flex-1 h-px" style={{ backgroundColor: theme.cardBorder }} />
              </div>

              <p className="text-sm leading-relaxed text-center" style={{ color: theme.text.muted }}>
                {t.historyPage.workouts.coachPath}
              </p>
            </>
          )}
        </div>
      )
    }
    return <Empty message={t.historyPage.workouts.entitlementRequired} theme={theme} />
  }
  if (items.length === 0) {
    return <Empty message={t.historyPage.workouts.empty} theme={theme} />
  }

  return (
    <div className="flex flex-col gap-3">
      {items.map(item => {
        const { summary } = item
        const expanded = expandedId === summary.id
        const duration = formatDuration(summary.startedAt, summary.finishedAt)
        const activeDetail = expanded && detail?.id === summary.id ? detail : null
        const exerciseCount = activeDetail?.exercises.length ?? null
        const isDeleting = deletingId === summary.id

        return (
          <div
            key={summary.id}
            className="rounded-2xl overflow-hidden"
            style={{ backgroundColor: theme.cardBg, border: `1px solid ${theme.cardBorder}` }}
          >
            <button
              type="button"
              onClick={() => onToggle(item)}
              className="w-full text-left px-4 py-3.5 active:opacity-70"
            >
              <div className="flex items-start gap-3">
                <div className="flex-1 min-w-0">
                  <p className="font-semibold truncate" style={{ color: theme.text.primary }}>
                    {summary.blockName || t.historyPage.workouts.unnamedBlock}
                  </p>
                  <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                    <span
                      className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold"
                      style={{ backgroundColor: theme.inputBgSubtle, color: theme.text.muted }}
                    >
                      {formatShortDate(new Date(summary.finishedAt), t)}
                      {duration ? ` · ${duration}` : ''}
                    </span>
                    {exerciseCount != null && (
                      <span
                        className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold tabular-nums"
                        style={{ backgroundColor: theme.inputBgSubtle, color: theme.text.muted }}
                      >
                        {t.trainingBlocks.exerciseCount_other.replace('{{count}}', String(exerciseCount))}
                      </span>
                    )}
                  </div>
                </div>
                <span
                  className="text-xl opacity-50 shrink-0 transition-transform duration-150"
                  style={{ transform: expanded ? 'rotate(90deg)' : 'rotate(0deg)' }}
                  aria-hidden
                >
                  →
                </span>
              </div>
            </button>

            {expanded && (
              <>
                {detailLoading && (
                  <div
                    className="px-4 py-3"
                    style={{ borderTop: `1px solid ${theme.cardBorder}` }}
                  >
                    <p className="text-sm text-center" style={{ color: theme.text.muted }}>
                      {t.historyPage.workouts.loadingDetail}
                    </p>
                  </div>
                )}
                {!detailLoading && activeDetail && activeDetail.exercises.length > 0 && (
                  <div style={{ borderTop: `1px solid ${theme.cardBorder}` }}>
                    {activeDetail.exercises.map((ex, idx, arr) => {
                      const sets = ex.setLogs ?? []
                      const isLast = idx === arr.length - 1
                      const displayIndex = ex.sortOrder != null ? ex.sortOrder + 1 : idx + 1
                      const plannedParts: string[] = []
                      if (ex.plannedWeight) plannedParts.push(ex.plannedWeight)
                      if (ex.plannedReps != null) plannedParts.push(`× ${ex.plannedReps}`)
                      else if (ex.plannedSets != null) plannedParts.push(`${ex.plannedSets} sets`)
                      const loggedSets = sets
                        .map((set, setIndex) => {
                          const weight = (set.weight ?? '').trim()
                          const reps = (set.reps ?? '').trim()
                          if (!weight && !reps) return null
                          const line = weight && reps
                            ? `${weight} × ${reps}`
                            : weight || `${reps} ${t.workout.repsSuffix}`
                          return { setIndex, line }
                        })
                        .filter((row): row is { setIndex: number; line: string } => row != null)
                      const comment = (ex.comment ?? '').trim()
                      const hasDetails = plannedParts.length > 0 || loggedSets.length > 0 || Boolean(comment)

                      return (
                        <div
                          key={ex.id}
                          className="flex items-start gap-3 px-4 py-3"
                          style={{ borderBottom: isLast ? undefined : `1px solid ${theme.cardBorder}` }}
                        >
                          <span
                            className="w-6 h-6 rounded-lg flex items-center justify-center text-xs font-bold shrink-0 mt-0.5"
                            style={{ backgroundColor: `${theme.buttonColor}18`, color: theme.accent.text }}
                          >
                            {displayIndex}
                          </span>
                          <div className="flex-1 min-w-0 space-y-2">
                            <p className="text-sm font-medium" style={{ color: theme.text.primary }}>
                              {ex.exerciseName}
                            </p>
                            {!hasDetails ? (
                              <p className="text-xs" style={{ color: theme.text.muted }}>
                                {t.workout.historyNoActuals}
                              </p>
                            ) : (
                              <>
                                {plannedParts.length > 0 && (
                                  <p className="text-xs" style={{ color: theme.text.muted }}>
                                    <span className="font-semibold uppercase tracking-wide">
                                      {t.historyPage.workouts.planned}
                                    </span>
                                    {' · '}
                                    <span className="tabular-nums">{plannedParts.join(' ')}</span>
                                  </p>
                                )}
                                {loggedSets.length > 0 && (
                                  <div>
                                    <div
                                      className="text-[10px] font-semibold uppercase tracking-wide mb-1"
                                      style={{ color: theme.text.muted }}
                                    >
                                      {t.workout.historySets}
                                    </div>
                                    <div className="flex flex-col gap-1">
                                      {loggedSets.map(({ setIndex, line }) => (
                                        <div key={setIndex} className="flex gap-2 text-sm leading-snug">
                                          <span
                                            className="w-5 shrink-0 tabular-nums font-medium"
                                            style={{ color: theme.text.muted }}
                                          >
                                            {setIndex + 1}.
                                          </span>
                                          <span
                                            className="font-semibold tabular-nums"
                                            style={{ color: theme.text.primary }}
                                          >
                                            {line}
                                          </span>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                )}
                                {comment && (
                                  <div>
                                    <div
                                      className="text-[10px] font-semibold uppercase tracking-wide mb-0.5"
                                      style={{ color: theme.text.muted }}
                                    >
                                      {t.workout.historyComment}
                                    </div>
                                    <p
                                      className="text-sm leading-snug whitespace-pre-wrap"
                                      style={{ color: theme.text.secondary }}
                                    >
                                      {comment}
                                    </p>
                                  </div>
                                )}
                              </>
                            )}
                          </div>
                        </div>
                      )
                    })}
                  </div>
                )}
                {!detailLoading && activeDetail && activeDetail.exercises.length === 0 && (
                  <div
                    className="px-4 py-3"
                    style={{ borderTop: `1px solid ${theme.cardBorder}` }}
                  >
                    <p className="text-sm text-center" style={{ color: theme.text.muted }}>
                      {t.trainingBlocks.noExercises}
                    </p>
                  </div>
                )}
              </>
            )}

            <div
              className="flex items-center justify-end px-3 py-2"
              style={{ borderTop: `1px solid ${theme.cardBorder}` }}
            >
              <button
                type="button"
                title={t.historyPage.workouts.deleteAria}
                onClick={() => onDelete(item)}
                disabled={isDeleting}
                className="p-3 rounded-xl transition-colors disabled:opacity-40 shrink-0"
                style={{ color: '#ef4444' }}
                aria-label={t.historyPage.workouts.deleteAria}
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                  <polyline points="3 6 5 6 21 6"/>
                  <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/>
                  <path d="M10 11v6"/><path d="M14 11v6"/>
                  <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/>
                </svg>
              </button>
            </div>
          </div>
        )
      })}
    </div>
  )
}

function Hint({ children, theme }: { children: string; theme: ReturnType<typeof useTheme> }) {
  return (
    <div
      className="rounded-xl px-3.5 py-3 mb-4 flex items-start gap-2.5"
      style={{
        backgroundColor: theme.isDark ? 'rgba(59,130,246,0.12)' : 'rgba(59,130,246,0.08)',
        border: '1px solid rgba(59,130,246,0.25)',
      }}
    >
      <p className="text-xs leading-relaxed" style={{ color: theme.text.secondary }}>
        {children}
      </p>
    </div>
  )
}

function Empty({ message, theme }: { message: string; theme: ReturnType<typeof useTheme> }) {
  return (
    <div className="flex flex-col items-center py-14 text-center">
      <p className="font-semibold" style={{ color: theme.text.primary }}>{message}</p>
    </div>
  )
}
